import { Box, type CustomerOrder, Flex, LoaderTalos, useDynamicCallback } from '@talos/kyoko';
import { useCallback, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import { DEFAULT_CARD_HEIGHT, WindowedExecutionCard, type WindowedExecutionCardDataProps } from './Card';

export const RecentOrdersList = ({
  recentOrders,
  handleCancelOrder,
  handleModifyOrder,
  handleResubmitOrder,
}: {
  handleCancelOrder: (orderID: string) => void;
  handleModifyOrder: (order: CustomerOrder) => void;
  handleResubmitOrder: (order: CustomerOrder) => void;
  recentOrders: CustomerOrder[] | undefined;
}) => {
  const listRef = useRef<VariableSizeList<any> | null>(null);

  const setRowHeight = useDynamicCallback((index: number, size: number) => {
    listRef.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [index]: size };
  });
  const rowHeights = useRef<Record<number, number>>({});
  const getRowHeight = useCallback((index: number) => rowHeights.current[index] || DEFAULT_CARD_HEIGHT, [rowHeights]);
  const itemData: WindowedExecutionCardDataProps = useMemo(
    () => ({
      items: recentOrders ?? [],
      setRowHeight,
      handleCancelOrder,
      handleModifyOrder,
      handleResubmitOrder,
      handleArchiveOrder: null,
    }),
    [recentOrders, setRowHeight, handleCancelOrder, handleModifyOrder, handleResubmitOrder]
  );

  if (!recentOrders) {
    return (
      <Flex alignItems="center" justifyContent="center" h="100%">
        <LoaderTalos delay={300} />
      </Flex>
    );
  }

  return recentOrders.length === 0 ? (
    <Flex alignItems="center" justifyContent="center" h="100%">
      No recent orders
    </Flex>
  ) : (
    <Box h="100%" background="backgroundBody" overflowX="hidden">
      <AutoSizer disableWidth>
        {({ height }) => (
          <VariableSizeList
            height={height}
            itemCount={recentOrders.length}
            itemSize={getRowHeight}
            itemData={itemData}
            ref={listRef}
            width="100%"
            style={{ overflowX: 'hidden', overflowY: 'auto' }}
          >
            {WindowedExecutionCard}
          </VariableSizeList>
        )}
      </AutoSizer>
    </Box>
  );
};
